import React from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { LANGUAGES } from '../../lib/constants'
import { getClient } from '../../lib/sanity.client'
import { LOCAL_SLUGS } from '../../lib/constants'

type AppLocale = 'de' | 'en' | 'es' | 'nl' | 'fr' | 'it'

type LanguageSelectorProps = {
  className?: string
}

/**
 * A component that allows users to select their preferred language.
 * Simplified version of LanguageSwitcher but matching the CurrencySelector design.
 */
export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  className = '',
}) => {
  const router = useRouter()
  const { i18n, t } = useTranslation('common')
  const client = getClient()

  const handleLanguageChange = async (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const lng = e.target.value as AppLocale
    const { pathname, query } = router

    const fetchSlugsPromises = Object.keys(query).map(async (key) => {
      const currentSlug = query[key]
      let newSlug
      if (
        LOCAL_SLUGS.find(
          (slug) => slug[i18n.language as AppLocale] === currentSlug,
        )
      ) {
        newSlug = LOCAL_SLUGS.find(
          (slug) => slug[i18n.language as AppLocale] === currentSlug,
        )?.[lng]
      } else {
        newSlug = await fetchSlugFromSanity(key, currentSlug as string, lng)
      }
      return { key, newSlug }
    })

    const slugsResults = await Promise.all(fetchSlugsPromises)
    let newPathname = pathname
    slugsResults.forEach(({ key, newSlug }) => {
      newPathname = newPathname.replace(`[${key}]`, newSlug)
    })

    router.push(newPathname, newPathname, { locale: lng })
    localStorage.setItem('next-i18next-locale', lng)
  }

  async function fetchSlugFromSanity(
    docType: string,
    currentSlug: string,
    locale: AppLocale,
  ) {
    const docTypesToQuery =
      docType === 'any' ? '"imprint", "privacy", "terms"' : `"${docType}"`
    const query = `*[ _type in [${docTypesToQuery}] && slug.${i18n.language}.current == $currentSlug]{ "newSlug": slug.${locale}.current }`
    const params = { currentSlug }
    const result = await client.fetch(query, params)

    return result[0]?.newSlug || '/'
  }

  return (
    <div className={`language-selector ${className}`}>
      <select
        value={i18n.language}
        onChange={handleLanguageChange}
        className="px-2 py-1 border border-gray-300 rounded"
        aria-label="Select language"
      >
        {LANGUAGES.map((lang) => (
          <option key={lang} value={lang}>
            {t(lang)}
          </option>
        ))}
      </select>
    </div>
  )
}

export default LanguageSelector
